import React, { useMemo, useState } from "react";

const LONG_STRING_LIMIT = 220;
const MAX_INLINE_ARRAY_ITEMS = 12;

/**
 * Best-effort parse: return objects/arrays as-is, unwrap JSON strings
 * (including truncated ones ending in …), leave plain text untouched.
 */
export function coerceStructuredValue(value) {
  if (value == null) return { kind: "empty", value: null, truncated: false };

  if (typeof value === "object") {
    return { kind: "json", value: deepParseStrings(value), truncated: false };
  }

  if (typeof value !== "string") {
    return { kind: "json", value, truncated: false };
  }

  const trimmed = value.trim();
  if (!trimmed) return { kind: "empty", value: null, truncated: false };

  const truncated = /[…\u2026]$/.test(trimmed) || trimmed.includes("…");
  const candidates = [trimmed];
  if (truncated) {
    const repaired = repairTruncatedJson(trimmed);
    if (repaired && repaired !== trimmed) candidates.unshift(repaired);
  }

  for (const candidate of candidates) {
    const parsed = tryParseJson(candidate);
    if (parsed !== undefined) {
      return {
        kind: "json",
        value: deepParseStrings(parsed),
        truncated,
      };
    }
  }

  // Double-encoded JSON string: "\"{...}\"" or "\"[...]\""
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    const inner = tryParseJson(trimmed);
    if (typeof inner === "string") {
      const nested = coerceStructuredValue(inner);
      if (nested.kind !== "empty") return { ...nested, truncated: truncated || nested.truncated };
    }
  }

  return { kind: "text", value: trimmed, truncated };
}

function tryParseJson(text) {
  if (!text || typeof text !== "string") return undefined;
  const t = text.trim();
  if (!(t.startsWith("{") || t.startsWith("["))) return undefined;
  try {
    return JSON.parse(t);
  } catch {
    return undefined;
  }
}

/**
 * Close truncated JSON enough for JSON.parse: strip ellipsis / mid-token junk,
 * then append missing } / ] closers.
 */
export function repairTruncatedJson(text) {
  if (!text || typeof text !== "string") return null;
  let s = text.trim().replace(/[…\u2026]+\s*$/, "");
  // Drop a trailing incomplete key/value fragment after the last safe comma/brace.
  s = s.replace(/,\s*"[^"]*$/, "");
  s = s.replace(/,\s*[^,[\]{}]*$/, "");
  s = s.replace(/:\s*"[^"]*$/, ': ""');
  s = s.replace(/:\s*-?\d+(\.\d*)?e?[+-]?\d*$/i, ": 0");
  s = s.replace(/:\s*(true|false|null)?$/i, ": null");

  const stack = [];
  let inString = false;
  let escape = false;
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (inString) {
      if (escape) escape = false;
      else if (ch === "\\") escape = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === "{") stack.push("}");
    else if (ch === "[") stack.push("]");
    else if (ch === "}" || ch === "]") stack.pop();
  }
  if (inString) s += '"';
  while (stack.length) s += stack.pop();
  return s;
}

function deepParseStrings(value, depth = 0) {
  if (depth > 6 || value == null) return value;
  if (typeof value === "string") {
    const parsed = tryParseJson(value);
    if (parsed !== undefined) return deepParseStrings(parsed, depth + 1);
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => deepParseStrings(item, depth + 1));
  }
  if (isPlainObject(value)) {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = deepParseStrings(v, depth + 1);
    }
    return out;
  }
  return value;
}

export function toPrettyJsonString(value) {
  const coerced = coerceStructuredValue(value);
  if (coerced.kind === "empty") return "";
  if (coerced.kind === "text") return coerced.value;
  try {
    return JSON.stringify(coerced.value, null, 2);
  } catch {
    return String(coerced.value);
  }
}

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function summarizeValue(value) {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    const oneLine = value.replace(/\s+/g, " ").trim();
    return oneLine.length > 56 ? `${oneLine.slice(0, 56)}…` : oneLine || '""';
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? "[]" : `[${value.length} item${value.length === 1 ? "" : "s"}]`;
  }
  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    return keys.length === 0 ? "{}" : `{${keys.length} field${keys.length === 1 ? "" : "s"}}`;
  }
  return String(value);
}

function looksLikeUrl(value) {
  return typeof value === "string" && /^https?:\/\/\S+$/i.test(value.trim());
}

function looksLikeId(key, value) {
  if (typeof value !== "string") return false;
  const k = String(key || "").toLowerCase();
  return (
    /(^|_)(id|scan_id|website_id|run_id|session_id|issue_id|test_name|status)$/.test(k) ||
    /^(scan|website|run|issue|session|gate)-/.test(value)
  );
}

/** Tokenize a pretty-printed JSON string into syntax-colored spans. */
export function highlightJson(pretty) {
  if (!pretty) return null;
  const parts = [];
  const re =
    /("(?:\\.|[^"\\])*")\s*:|("(?:\\.|[^"\\])*")|(\btrue\b|\bfalse\b)|(\bnull\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],:])|(\s+)/g;
  let last = 0;
  let match = re.exec(pretty);
  while (match) {
    if (match.index > last) {
      parts.push({ text: pretty.slice(last, match.index), className: "text-gray-700" });
    }
    if (match[1] != null) {
      parts.push({ text: match[1], className: "text-violet-700 font-semibold" });
      parts.push({ text: ":", className: "text-gray-400" });
    } else if (match[2] != null) {
      parts.push({ text: match[2], className: "text-emerald-700" });
    } else if (match[3] != null) {
      parts.push({
        text: match[3],
        className: match[3] === "true" ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold",
      });
    } else if (match[4] != null) {
      parts.push({ text: match[4], className: "text-gray-400 italic" });
    } else if (match[5] != null) {
      parts.push({ text: match[5], className: "text-sky-700" });
    } else if (match[6] != null) {
      parts.push({ text: match[6], className: "text-gray-400" });
    } else if (match[7] != null) {
      parts.push({ text: match[7], className: "" });
    }
    last = re.lastIndex;
    match = re.exec(pretty);
  }
  if (last < pretty.length) {
    parts.push({ text: pretty.slice(last), className: "text-gray-700" });
  }
  return parts;
}

function JsonHighlight({ text }) {
  const parts = useMemo(() => highlightJson(text), [text]);
  if (!parts) return null;
  return (
    <pre className="text-[12px] leading-5 font-mono whitespace-pre-wrap break-words m-0">
      {parts.map((part, i) => (
        <span key={`tok-${i}`} className={part.className || undefined}>
          {part.text}
        </span>
      ))}
    </pre>
  );
}

function PrimitiveValue({ value, fieldKey }) {
  if (value === null) {
    return <span className="font-mono text-gray-400 italic">null</span>;
  }
  if (typeof value === "boolean") {
    return (
      <span
        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold font-mono border ${
          value
            ? "bg-emerald-50 text-emerald-700 border-emerald-100"
            : "bg-rose-50 text-rose-700 border-rose-100"
        }`}
      >
        {value ? "true" : "false"}
      </span>
    );
  }
  if (typeof value === "number") {
    return <span className="font-mono text-sky-700 tabular-nums">{value}</span>;
  }
  if (typeof value === "string") {
    if (looksLikeUrl(value)) {
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-indigo-600 hover:text-indigo-800 underline break-all"
        >
          {value}
        </a>
      );
    }
    if (looksLikeId(fieldKey, value)) {
      return (
        <code className="inline-flex max-w-full items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 border border-slate-200 font-mono text-[11px] break-all">
          {value}
        </code>
      );
    }
    return <ExpandableString text={value} />;
  }
  return <span className="font-mono text-gray-700 break-all">{String(value)}</span>;
}

function ExpandableString({ text }) {
  const [open, setOpen] = useState(false);
  const isLong = text.length > LONG_STRING_LIMIT;

  if (!isLong) {
    return <span className="text-gray-800 break-words whitespace-pre-wrap">{text || '""'}</span>;
  }

  return (
    <span className="block min-w-0">
      <span className="text-gray-800 break-words whitespace-pre-wrap">
        {open ? text : `${text.slice(0, LONG_STRING_LIMIT)}…`}
      </span>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ml-2 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 underline focus:outline-none"
      >
        {open ? "Show less" : `Show more (${text.length.toLocaleString()} chars)`}
      </button>
    </span>
  );
}

function NestToggle({ open, onToggle, summary }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="inline-flex items-center gap-1.5 text-left text-[11px] font-semibold text-gray-600 hover:text-gray-900 focus:outline-none"
      aria-expanded={open}
    >
      <span
        className={`inline-flex h-4 w-4 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 transition-transform ${
          open ? "rotate-90" : ""
        }`}
      >
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
      </span>
      <span className="font-mono text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
        {summary}
      </span>
    </button>
  );
}

function ObjectRows({ value, depth }) {
  const entries = Object.entries(value);
  if (entries.length === 0) {
    return <span className="font-mono text-gray-400 text-xs">{"{}"}</span>;
  }

  if (depth === 0) {
    return (
      <div className="space-y-2">
        {entries.map(([key, child]) => (
          <div
            key={key}
            className="rounded-lg border border-gray-100 bg-slate-50/70 overflow-hidden"
          >
            <div className="px-3 py-1.5 border-b border-gray-100 bg-white/80 flex items-center gap-2">
              <span className="text-[11px] font-bold font-mono text-violet-700">{key}</span>
              {(isPlainObject(child) || Array.isArray(child)) && (
                <span className="text-[10px] font-mono text-gray-400">{summarizeValue(child)}</span>
              )}
            </div>
            <div className="px-3 py-2 text-xs text-gray-800 min-w-0">
              <StructuredNode value={child} depth={depth + 1} fieldKey={key} defaultOpen={depth < 1} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="min-w-0 ml-1 border-l border-gray-100 pl-3">
      <dl className="divide-y divide-gray-100/80">
        {entries.map(([key, child]) => (
          <div
            key={key}
            className="grid grid-cols-1 sm:grid-cols-[minmax(7rem,11rem)_minmax(0,1fr)] gap-1 sm:gap-3 py-1.5 first:pt-0 last:pb-0"
          >
            <dt className="text-[11px] font-semibold text-gray-500 font-mono break-all pt-0.5">{key}</dt>
            <dd className="min-w-0 text-xs text-gray-800">
              <StructuredNode value={child} depth={depth + 1} fieldKey={key} />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function ArrayRows({ value, depth }) {
  if (value.length === 0) {
    return <span className="font-mono text-gray-400 text-xs">[]</span>;
  }

  const allPrimitive = value.every(
    (item) => item == null || ["string", "number", "boolean"].includes(typeof item)
  );

  if (allPrimitive && value.length <= MAX_INLINE_ARRAY_ITEMS) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {value.map((item, index) => (
          <span
            key={`arr-${index}`}
            className="inline-flex max-w-full items-center rounded-md border border-gray-200 bg-white px-1.5 py-0.5"
          >
            <PrimitiveValue value={item} fieldKey={String(index)} />
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className={`min-w-0 space-y-2 ${depth > 0 ? "ml-1 border-l border-gray-100 pl-3" : ""}`}>
      {value.map((item, index) => (
        <div
          key={`arr-row-${index}`}
          className="rounded-lg border border-gray-100 bg-white px-2.5 py-2"
        >
          <div className="text-[10px] font-mono text-gray-400 mb-1">[{index}]</div>
          <StructuredNode value={item} depth={depth + 1} fieldKey={String(index)} defaultOpen />
        </div>
      ))}
    </div>
  );
}

function StructuredNode({ value, depth = 0, fieldKey = "", defaultOpen }) {
  const isObject = isPlainObject(value);
  const isArray = Array.isArray(value);
  const nestable = isObject || isArray;
  const openByDefault =
    defaultOpen != null
      ? defaultOpen
      : depth < 2 || (isObject && Object.keys(value).length <= 8) || (isArray && value.length <= 4);
  const [open, setOpen] = useState(openByDefault);

  if (!nestable) {
    return <PrimitiveValue value={value} fieldKey={fieldKey} />;
  }

  const summary = summarizeValue(value);
  const empty = isArray ? value.length === 0 : Object.keys(value).length === 0;

  if (empty) {
    return <span className="font-mono text-gray-400 text-xs">{summary}</span>;
  }

  if (depth === 0) {
    return isArray ? <ArrayRows value={value} depth={depth} /> : <ObjectRows value={value} depth={depth} />;
  }

  // Nested containers: show a compact toggle, expand by default for small ones.
  if (depth === 1 && (isObject || isArray)) {
    return isArray ? <ArrayRows value={value} depth={depth} /> : <ObjectRows value={value} depth={depth} />;
  }

  return (
    <div className="min-w-0">
      <NestToggle open={open} onToggle={() => setOpen((v) => !v)} summary={summary} />
      {open && (
        <div className="mt-1.5">
          {isArray ? <ArrayRows value={value} depth={depth} /> : <ObjectRows value={value} depth={depth} />}
        </div>
      )}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        } catch {
          /* ignore */
        }
      }}
      className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 hover:text-indigo-700 focus:outline-none"
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function formatRefusalResult(text) {
  if (!text || typeof text !== "string") return null;
  const match = text.match(/^(.*?REFUSED:\s*)(.+?)(?:\s*\[(.*?)\])?\s*$/is);
  if (!match) return null;
  const [, prefix, reason, listBody] = match;
  const ids = listBody
    ? listBody
        .split(",")
        .map((s) => s.trim().replace(/^['"]|['"]$/g, ""))
        .filter(Boolean)
    : [];
  return { prefix: prefix.trim(), reason: reason.trim(), ids };
}

function RefusalResultView({ text }) {
  const parsed = formatRefusalResult(text);
  if (!parsed) {
    return (
      <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap break-words leading-relaxed m-0">
        {text}
      </pre>
    );
  }
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-red-100 bg-red-50/70 px-3 py-2">
        <div className="text-[10px] font-bold uppercase tracking-wider text-red-500 mb-1">Refused</div>
        <div className="text-sm text-red-900 font-medium leading-relaxed">{parsed.reason}</div>
      </div>
      {parsed.ids.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
            Missing step ids ({parsed.ids.length})
          </div>
          <ul className="space-y-1">
            {parsed.ids.map((id) => (
              <li key={id}>
                <code className="inline-flex text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-800">
                  {id}
                </code>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Pretty, scannable panel for tool-call arguments or execution results.
 */
export function ToolPayloadPanel({ label, value, emptyLabel = "(empty)", showHeader = true }) {
  const [viewMode, setViewMode] = useState("pretty");
  const coerced = useMemo(() => coerceStructuredValue(value), [value]);
  const prettyText = useMemo(() => toPrettyJsonString(value), [value]);
  const isEmpty = coerced.kind === "empty";
  const showModeToggle = !isEmpty && coerced.kind === "json";
  const showToolbar = showHeader || showModeToggle || !!prettyText;
  const isRefusalText =
    coerced.kind === "text" && typeof coerced.value === "string" && /REFUSED:/i.test(coerced.value);

  return (
    <div className="min-w-0">
      {showToolbar && (
        <div className={`mb-1.5 flex items-center gap-2 ${showHeader ? "" : "justify-end"}`}>
          {showHeader && (
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {label}
              </div>
              {coerced.truncated && (
                <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                  Truncated
                </span>
              )}
            </div>
          )}
          <div className={`${showHeader ? "ml-auto" : ""} flex items-center gap-2`}>
            {showModeToggle && (
              <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
                <button
                  type="button"
                  onClick={() => setViewMode("pretty")}
                  className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                    viewMode === "pretty"
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Fields
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("raw")}
                  className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                    viewMode === "raw"
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  JSON
                </button>
              </div>
            )}
            <CopyButton text={prettyText} />
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm max-h-[28rem] overflow-auto">
        {isEmpty ? (
          <div className="text-xs text-gray-400 italic">{emptyLabel}</div>
        ) : isRefusalText ? (
          <RefusalResultView text={coerced.value} />
        ) : coerced.kind === "text" ? (
          <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap break-words leading-relaxed m-0">
            {prettyText}
          </pre>
        ) : viewMode === "raw" ? (
          <JsonHighlight text={prettyText} />
        ) : (
          <StructuredNode value={coerced.value} depth={0} defaultOpen />
        )}
      </div>
    </div>
  );
}

export default function ToolCallDetails({ args, result, isError = false }) {
  return (
    <div
      className={`rounded-xl border p-4 space-y-4 mt-1 ${
        isError ? "bg-red-50/40 border-red-100" : "bg-slate-50/60 border-gray-100"
      }`}
    >
      <ToolPayloadPanel label="Arguments" value={args} emptyLabel="{}" />
      <ToolPayloadPanel label="Execution Result" value={result} emptyLabel="(empty result)" />
    </div>
  );
}
